import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { FactionIcon } from "../../../components/icons/FactionIcon";

const STAT_COLORS = (isDark) => [
  isDark ? "#9ca3af" : "#6b7280",
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706"
];

const CHILD_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706",
  isDark ? "#f87171" : "#dc2626",
  isDark ? "#22d3ee" : "#0891b2"
];

function getValueColor(value, isDark) {
  if (value >= 55) return isDark ? "#4ade80" : "#16a34a";
  if (value <= 45) return isDark ? "#f87171" : "#dc2626";
  return isDark ? "#9ca3af" : "#6b7280";
}

function ListRow({ row, isSelected, isLast, isDark, theme, onSelect }) {
  const color = getValueColor(row.value, isDark);
  return (
    <Box>
      <Box
        onClick={onSelect}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 1.25,
          px: 1,
          borderRadius: 1,
          cursor: "pointer",
          borderLeft: `3px solid ${isSelected ? "#3b82f6" : "transparent"}`,
          backgroundColor: isSelected
            ? isDark
              ? "rgba(59,130,246,0.1)"
              : "rgba(37,99,235,0.06)"
            : "transparent",
          "&:hover": {
            backgroundColor: isSelected
              ? isDark
                ? "rgba(59,130,246,0.14)"
                : "rgba(37,99,235,0.09)"
              : isDark
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)"
          }
        }}
      >
        <FactionIcon
          icon={row.icon}
          name={row.label}
          size={28}
          sx={{ fontSize: "0.6rem", flexShrink: 0 }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: isSelected ? 700 : 500,
              fontSize: "0.8rem",
              color: isSelected
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
              mb: 0.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {row.label}
          </Typography>
          <Box
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${row.value}%`,
                borderRadius: 2,
                background: `linear-gradient(90deg, ${color}55, ${color})`
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
          <Typography
            sx={{
              color,
              fontWeight: 700,
              fontSize: "0.8rem",
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {row.value}%
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.65rem",
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {row.games} games
          </Typography>
        </Box>
      </Box>

      {!isLast && (
        <Divider
          sx={{
            borderColor: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.04)"
          }}
        />
      )}
    </Box>
  );
}

function DetailStatRow({ stat, labelColor, dataType, isDark, theme, isLast }) {
  const valueColor = getValueColor(stat.value, isDark);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
        <Box
          sx={{
            width: 68,
            flexShrink: 0,
            px: 0.75,
            py: 0.35,
            borderRadius: 0.75,
            border: `1px solid ${labelColor}44`,
            backgroundColor: `${labelColor}18`,
            textAlign: "center"
          }}
        >
          <Typography
            sx={{
              color: labelColor,
              fontSize: "0.6rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              lineHeight: 1.3
            }}
          >
            {stat.label}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 0.75
            }}
          >
            <Typography
              sx={{ color: valueColor, fontWeight: 700, fontSize: "0.875rem" }}
            >
              {stat.value}% {dataType}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
                fontVariantNumeric: "tabular-nums"
              }}
            >
              {stat.games?.toLocaleString()} games
            </Typography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${stat.value}%`,
                borderRadius: 3,
                background: `linear-gradient(90deg, ${valueColor}55, ${valueColor})`
              }}
            />
          </Box>
        </Box>
      </Box>
      {!isLast && (
        <Divider
          sx={{
            borderColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.06)"
          }}
        />
      )}
    </Box>
  );
}

function ChildRow({ child, color, dataType, isDark, theme, isLast }) {
  const valueColor = getValueColor(child.value, isDark);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
        <FactionIcon
          icon={child.icon}
          name={child.label}
          size={30}
          sx={{
            fontSize: "0.65rem",
            backgroundColor: !child.icon ? color : undefined,
            flexShrink: 0
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 0.5
            }}
          >
            <Typography sx={{ color: valueColor, fontWeight: 700, fontSize: "0.8rem" }}>
              {child.value}% {dataType}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.7rem",
                fontVariantNumeric: "tabular-nums"
              }}
            >
              {child.games?.toLocaleString()} games
            </Typography>
          </Box>
          <Box
            sx={{
              height: 5,
              borderRadius: 3,
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
              overflow: "hidden",
              mb: 0.4
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${child.value}%`,
                borderRadius: 3,
                background: `linear-gradient(90deg, ${valueColor}55, ${valueColor})`
              }}
            />
          </Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
              fontWeight: 500
            }}
          >
            {child.label}
          </Typography>
        </Box>
      </Box>
      {!isLast && (
        <Divider
          sx={{
            borderColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.06)"
          }}
        />
      )}
    </Box>
  );
}

export default function DashboardIconChart({ label, data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    listLabel,
    detailPrefix,
    childrenLabel,
    dataType = "Win Rate"
  } = data || {};
  const lowerDataType = dataType.toLowerCase();
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (data?.rows) {
      const sorted = [...data.rows].sort((a, b) => b.value - a.value);
      setRows(sorted);
      setSelectedId(sorted?.[0]?.id ?? null);
    } else {
      setRows([]);
      setSelectedId(null);
    }
  }, [data]);

  const selected = rows.find((row) => row.id === selectedId) || rows[0];

  const containerSx = {
    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 1,
    p: { xs: 1.5, sm: 2.5 },
    boxShadow: isDark
      ? "0 2px 8px rgba(0,0,0,0.3)"
      : "0 2px 8px rgba(0,0,0,0.1)"
  };

  if (!rows.length) return null;

  return (
    <>
      {label?.length ? (
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            color: "primary",
            fontWeight: 600,
            textAlign: "center",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            pt: 2
          }}
        >
          {label}
        </Typography>
      ) : (
        <></>
      )}

      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        <Grid item xs={12} md={5} sx={{ display: "flex" }}>
          <Box
            sx={{
              ...containerSx,
              flex: 1,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
            >
              {listLabel || "All Matchups"}
            </Typography>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {rows.map((row, i) => (
                <ListRow
                  key={row.id || i}
                  row={row}
                  isSelected={selected?.id === row.id}
                  isLast={i === rows.length - 1}
                  isDark={isDark}
                  theme={theme}
                  onSelect={() => setSelectedId(row.id)}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7} sx={{ display: "flex" }}>
          {selected && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
              <Box sx={containerSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                    {detailPrefix || "vs"}
                  </Typography>
                  <FactionIcon
                    icon={selected.icon}
                    name={selected.label}
                    size={28}
                    sx={{ fontSize: "0.6rem" }}
                  />
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                    {selected.label}
                  </Typography>
                </Box>

                {selected.stats?.map((stat, i) => (
                  <DetailStatRow
                    key={stat.label || i}
                    stat={stat}
                    labelColor={STAT_COLORS(isDark)[i % STAT_COLORS(isDark).length]}
                    dataType={lowerDataType}
                    isDark={isDark}
                    theme={theme}
                    isLast={i === selected.stats.length - 1}
                  />
                ))}
              </Box>

              {selected.children?.length > 0 && (
                <Box sx={{ ...containerSx, flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
                  >
                    {detailPrefix || "vs"} {selected.label}
                    {childrenLabel ? ` ${childrenLabel}` : ""}
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {selected.children.map((child, i) => (
                      <ChildRow
                        key={child.id || i}
                        child={child}
                        color={CHILD_COLORS(isDark)[i % CHILD_COLORS(isDark).length]}
                        dataType={lowerDataType}
                        isDark={isDark}
                        theme={theme}
                        isLast={i === selected.children.length - 1}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}
